import * as React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { DashboardPageFormBody, DashboardPageFormField } from '@shigo-ui/dashboard-ui';
import {
  AutocompleteSingleSelect,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@shigo-ui/ui';

import { dashboardT, type DashboardMessageKey } from '../../../../../lib/messages';
import { usePlacementPublishersQuery } from '../../hooks';
import {
  PlacementDeliveryType,
  PlacementStatus,
  resolvePlacementStatusLabel,
  type PlacementPublisher,
} from '../../model';
import type { PlacementFormValue } from '../../placementFormSchema';
import { PlacementImpressionTrackersFields } from './PlacementImpressionTrackersFields';
import { PlacementFieldLabel } from './PlacementFieldLabel';
import { PlacementPoddingSchainFields } from './PlacementPoddingSchainFields';
import { PlacementRequestIvtFields } from './PlacementRequestIvtFields';

const deliveryLabelKeys: Record<PlacementDeliveryType, DashboardMessageKey> = {
  [PlacementDeliveryType.App]: 'placements.delivery.app',
  [PlacementDeliveryType.Ctv]: 'placements.delivery.ctv',
  [PlacementDeliveryType.Web]: 'placements.delivery.web',
};

const numberFields = [
  {
    name: 'bidFloor',
    tooltip: 'placements.tooltips.bidFloor',
    max: undefined,
    suffix: undefined,
  },
  {
    name: 'qpsLimit',
    tooltip: 'placements.tooltips.qpsLimit',
    max: undefined,
    suffix: undefined,
  },
  {
    name: 'maxAdDuration',
    tooltip: 'placements.tooltips.maxAdDuration',
    max: 5787,
    suffix: 'placements.secondsSuffix',
  },
] as const satisfies ReadonlyArray<{
  name: 'bidFloor' | 'qpsLimit' | 'maxAdDuration';
  tooltip: DashboardMessageKey;
  max: number | undefined;
  suffix: DashboardMessageKey | undefined;
}>;

export function PlacementGeneralFields(props: {
  editing: boolean;
  initialPublisher?: PlacementPublisher | undefined;
}) {
  'use no memo';
  const { control, register, formState } = useFormContext<PlacementFormValue>();
  const [publisherSearch, setPublisherSearch] = React.useState('');
  const publisherQuery = usePlacementPublishersQuery(
    { searchTerm: publisherSearch, page: 0, size: 25 },
    !props.editing && publisherSearch.trim().length > 0,
  );
  const publisherOptions = React.useMemo(() => {
    const options = (publisherQuery.data?.content ?? []).map((publisher) => ({
      id: publisher.id,
      label: publisher.name,
    }));
    if (
      props.initialPublisher &&
      !options.some((option) => option.id === props.initialPublisher?.id)
    ) {
      options.unshift({ id: props.initialPublisher.id, label: props.initialPublisher.name });
    }
    return options;
  }, [props.initialPublisher, publisherQuery.data?.content]);
  return (
    <DashboardPageFormBody width="lg">
      <div className="grid gap-5 md:grid-cols-2">
        <DashboardPageFormField
          label={dashboardT('placements.name')}
          htmlFor="placement-name"
          required
          error={formState.errors.name?.message}
        >
          <Input
            id="placement-name"
            required
            aria-invalid={Boolean(formState.errors.name)}
            {...register('name')}
          />
        </DashboardPageFormField>
        <Controller
          control={control}
          name="publisherId"
          render={({ field, fieldState }) => (
            <DashboardPageFormField
              label={dashboardT('placements.publisher')}
              required
              error={fieldState.error?.message}
            >
              <AutocompleteSingleSelect
                label={dashboardT('placements.publisher')}
                options={publisherOptions}
                value={field.value}
                onValueChange={field.onChange}
                onSearchTermChange={setPublisherSearch}
                searchDebounceMs={650}
                loading={publisherQuery.isFetching}
                disabled={props.editing}
                minSearchLength={1}
                placeholder={dashboardT('placements.selectPublisher')}
                searchPlaceholder={dashboardT('placements.searchPublishers')}
                emptyMessage={dashboardT('placements.noPublishers')}
              />
            </DashboardPageFormField>
          )}
        />
        <Controller
          control={control}
          name="deliveryType"
          render={({ field }) => (
            <DashboardPageFormField
              label={
                <PlacementFieldLabel
                  label={dashboardT('placements.deliveryType')}
                  tooltip={dashboardT('placements.tooltips.deliveryType')}
                />
              }
            >
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger aria-label={dashboardT('placements.deliveryType')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(PlacementDeliveryType).map((type) => (
                    <SelectItem key={type} value={type}>
                      {dashboardT(deliveryLabelKeys[type])}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </DashboardPageFormField>
          )}
        />
        <Controller
          control={control}
          name="status"
          render={({ field }) => (
            <DashboardPageFormField label={dashboardT('placements.status')} required>
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger aria-label={dashboardT('placements.status')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(PlacementStatus).map((status) => (
                    <SelectItem key={status} value={status}>
                      {resolvePlacementStatusLabel(status)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </DashboardPageFormField>
          )}
        />
        {numberFields.map(({ name, tooltip, max, suffix }) => (
          <DashboardPageFormField
            key={name}
            label={
              <PlacementFieldLabel
                label={dashboardT(`placements.${name}`)}
                tooltip={dashboardT(tooltip)}
              />
            }
            htmlFor={`placement-${name}`}
            error={formState.errors[name]?.message}
          >
            <div className="relative">
              <Input
                id={`placement-${name}`}
                type="number"
                min="0"
                max={max}
                className={suffix ? 'pr-20' : undefined}
                aria-invalid={Boolean(formState.errors[name])}
                {...register(name)}
              />
              {suffix ? (
                <span className="text-muted-foreground pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm">
                  {dashboardT(suffix)}
                </span>
              ) : null}
            </div>
          </DashboardPageFormField>
        ))}
      </div>
      <DashboardPageFormField label={dashboardT('placements.notes')} htmlFor="placement-notes">
        <Textarea id="placement-notes" rows={3} {...register('notes')} />
      </DashboardPageFormField>

      <PlacementRequestIvtFields />
      <PlacementImpressionTrackersFields />
      <PlacementPoddingSchainFields />
    </DashboardPageFormBody>
  );
}
